import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight } from "lucide-react";
import AuthShell from "../components/AuthShell";

const inputCls =
  "w-full mt-1.5 px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/30 focus:border-[#34C97B] focus:ring-2 focus:ring-[#34C97B]/30 outline-none transition";

export default function Login() {
  const { login, loginWithOTP } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("+91 ");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const roleHome = (role) =>
    ({
      super_admin: "/super-admin",
      staff: "/staff",
      vendor: "/vendors/dashboard",
      admin: "/dashboard",
      owner: "/dashboard",
      student: "/dashboard",
    })[role] || "/dashboard";

  const submitEmail = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back!");
      const target = loc.state?.from || roleHome(r.user?.role);
      nav(target);
    } else setErr(r.error);
  };

  const sendOtp = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone });
      setOtpSent(true);
      setDevOtp(data.dev_otp || "");
      if (data.sms_sent) toast.success("OTP sent via SMS — check your phone");
      else toast("Twilio couldn't deliver — using dev OTP below.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await loginWithOTP({ phone, otp });
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome!");
      nav(roleHome(r.user?.role));
    } else setErr(r.error);
  };

  return (
    <AuthShell
      footer={
        <>
          New to Hostro?{" "}
          <Link to="/register" className="font-semibold text-[#34C97B] hover:underline" data-testid="link-register">
            Create an account
          </Link>
        </>
      }
    >
      <div data-testid="login-page">
        <h1 className="text-2xl font-bold text-white">Welcome back</h1>
        <p className="text-sm text-white/60 mt-1">Sign in to continue to your Hostro account</p>

        {/* Tab switch */}
        <div className="flex gap-1 p-1 bg-white/5 border border-white/10 rounded-xl mt-6 mb-5">
          <button
            onClick={() => setMode("email")}
            className={`flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg transition ${
              mode === "email" ? "bg-[#34C97B] text-white shadow" : "text-white/60 hover:text-white"
            }`}
            data-testid="login-tab-email"
          >
            <Mail size={14} /> Email
          </button>
          <button
            onClick={() => setMode("otp")}
            className={`flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg transition ${
              mode === "otp" ? "bg-[#34C97B] text-white shadow" : "text-white/60 hover:text-white"
            }`}
            data-testid="login-tab-otp"
          >
            <Phone size={14} /> Phone OTP
          </button>
        </div>

        {mode === "email" ? (
          <form onSubmit={submitEmail} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Email</label>
              <input
                className={inputCls}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Password</label>
              <input
                className={inputCls}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            {err && (
              <div
                className="bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-xl p-3 text-sm font-medium"
                data-testid="login-error"
              >
                {err}
              </div>
            )}
            <button
              className="w-full inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition"
              disabled={busy}
              data-testid="login-submit"
            >
              {busy ? "Signing in..." : (<>Sign in <ArrowRight size={16} /></>)}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Phone number</label>
              <input
                className={inputCls}
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 9876543210"
                data-testid="login-phone"
              />
            </div>
            {!otpSent ? (
              <button
                type="button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition"
                disabled={busy}
                onClick={sendOtp}
                data-testid="send-otp-btn"
              >
                {busy ? "Sending..." : "Send OTP"}
              </button>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Enter OTP</label>
                  <input
                    className={`${inputCls} tracking-widest text-center text-2xl font-bold`}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength={6}
                    data-testid="login-otp"
                  />
                </div>
                {devOtp && (
                  <div
                    className="bg-[#34C97B]/10 border border-[#34C97B]/30 rounded-xl p-3 text-sm"
                    data-testid="dev-otp-display"
                  >
                    <strong className="text-[#34C97B]">Dev OTP:</strong>{" "}
                    <code className="font-bold text-2xl tracking-widest ml-2 text-white">{devOtp}</code>
                    <div className="text-xs text-white/60 mt-1">Copy OTP into the field above.</div>
                  </div>
                )}
                {err && (
                  <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-xl p-3 text-sm font-medium">
                    {err}
                  </div>
                )}
                <button
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition"
                  disabled={busy}
                  data-testid="verify-otp-btn"
                >
                  {busy ? "Verifying..." : "Verify & sign in"}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </AuthShell>
  );
}
